/**
 * Panel Animation Coordinator - GSAP Edition
 * High-performance iPad-optimized panel animations with proper cleanup
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { ref, computed, onBeforeUnmount } from 'vue'
import { gsap } from 'gsap'

type AnimationState = 'idle' | 'opening' | 'closing' | 'switching'
type PanelSide = 'left' | 'right'

// Global animation state to prevent conflicts
const globalAnimationState = ref<AnimationState>('idle')
const animatingPanel = ref<PanelSide | null>(null)
const animationQueue = ref<Array<() => Promise<void>>>([])

// GSAP timeline tracking for cleanup
const activeTimelines = new Set<gsap.core.Timeline>()

export function usePanelAnimations() {
  
  /**
   * Create optimized GSAP timeline for iPad performance
   */
  function createPanelTimeline(): gsap.core.Timeline {
    const timeline = gsap.timeline({
      // iPad-optimized settings
      force3D: true,
      lazy: false,
      autoRemoveChildren: true,
      smoothChildTiming: true,
      onComplete: () => {
        // Auto-cleanup completed timelines
        activeTimelines.delete(timeline)
      }
    })
    
    activeTimelines.add(timeline)
    return timeline
  }
  
  /**
   * Execute panel animation with GSAP and proper sequencing
   */
  async function executeAnimation(
    panelElement: HTMLElement,
    side: PanelSide,
    action: 'open' | 'close',
    isImmediate = false
  ): Promise<void> {
    
    // If another animation is running, queue this one
    if (globalAnimationState.value !== 'idle' && !isImmediate) {
      return new Promise((resolve) => {
        animationQueue.value.push(async () => {
          await executeAnimation(panelElement, side, action, true)
          resolve()
        })
      })
    }
    
    // Set animation state
    globalAnimationState.value = action === 'open' ? 'opening' : 'closing'
    animatingPanel.value = side
    
    try {
      if (action === 'open') {
        await animateOpen(panelElement, side)
      } else {
        await animateClose(panelElement, side)
      }
    } finally {
      // Reset state and process queue
      globalAnimationState.value = 'idle'
      animatingPanel.value = null
      processQueue()
    }
  }
  
  /**
   * GSAP-powered panel opening animation
   * Optimized for iPad with hardware acceleration
   */
  async function animateOpen(panelElement: HTMLElement, side: PanelSide): Promise<void> {
    const timeline = createPanelTimeline()
    
    // Immediately set z-index and add animation class
    panelElement.style.zIndex = '102'
    panelElement.classList.add('animating-open')
    
    // GSAP animation - animate from current position to open
    timeline.to(panelElement, {
      x: '0%',
      duration: 0.25,
      ease: 'power2.out', // Equivalent to cubic-bezier(0.25, 0.1, 0.25, 1)
      force3D: true,
      transformOrigin: 'center center',
      // iPad-specific optimizations
      rotationZ: 0.01, // Force layer creation
      scale: 1, // Prevent sub-pixel rendering issues
    })
    
    // Wait for animation completion
    await new Promise<void>((resolve) => {
      timeline.eventCallback('onComplete', () => {
        panelElement.classList.remove('animating-open')
        resolve()
      })
    })
  }
  
  /**
   * GSAP-powered panel closing animation
   */
  async function animateClose(panelElement: HTMLElement, side: PanelSide): Promise<void> {
    const timeline = createPanelTimeline()
    
    panelElement.classList.add('animating-close')
    
    // GSAP animation - animate from current position to closed
    const targetX = side === 'left' ? '-100%' : '100%'
    
    timeline.to(panelElement, {
      x: targetX,
      duration: 0.25,
      ease: 'power2.out',
      force3D: true,
      transformOrigin: 'center center',
      rotationZ: 0.01,
      scale: 1,
    })
    
    // Wait for animation completion
    await new Promise<void>((resolve) => {
      timeline.eventCallback('onComplete', () => {
        // Lower z-index after animation completes
        panelElement.style.zIndex = '100'
        panelElement.classList.remove('animating-close')
        resolve()
      })
    })
  }
  
  /**
   * Switch between panels with staggered GSAP animation
   */
  async function switchPanels(
    closingElement: HTMLElement,
    openingElement: HTMLElement,
    closingSide: PanelSide,
    openingSide: PanelSide
  ): Promise<void> {
    if (globalAnimationState.value !== 'idle') {
      return executeAnimation(openingElement, openingSide, 'open')
    }
    
    globalAnimationState.value = 'switching'
    
    try {
      const masterTimeline = createPanelTimeline()
      
      // Set z-indexes immediately
      closingElement.style.zIndex = '102'
      openingElement.style.zIndex = '102'
      closingElement.classList.add('animating-close')
      openingElement.classList.add('animating-open')
      
      const closeX = closingSide === 'left' ? '-100%' : '100%'
      const openFromX = openingSide === 'left' ? '-100%' : '100%'
      
      // Staggered animation timeline
      masterTimeline
        // Start closing current panel
        .to(closingElement, {
          x: closeX,
          duration: 0.25,
          ease: 'power2.out',
          force3D: true,
          transformOrigin: 'center center',
          rotationZ: 0.01,
          scale: 1,
        }, 0)
        // Start opening new panel with 100ms stagger
        .fromTo(openingElement,
          {
            x: openFromX,
            force3D: true
          },
          {
            x: '0%',
            duration: 0.25,
            ease: 'power2.out',
            force3D: true,
            transformOrigin: 'center center',
            rotationZ: 0.01,
            scale: 1,
          }, 0.1 // 100ms stagger
        )
      
      // Wait for master timeline completion
      await new Promise<void>((resolve) => {
        masterTimeline.eventCallback('onComplete', () => {
          // Cleanup z-indexes and classes
          closingElement.style.zIndex = '100'
          openingElement.style.zIndex = '102'
          closingElement.classList.remove('animating-close')
          openingElement.classList.remove('animating-open')
          resolve()
        })
      })
    } finally {
      globalAnimationState.value = 'idle'
      animatingPanel.value = null
      processQueue()
    }
  }
  
  /**
   * Route transition animation - panels slide extra distance
   */
  async function animateRouteTransition(
    panelElement: HTMLElement,
    side: PanelSide,
    entering: boolean
  ): Promise<void> {
    const timeline = createPanelTimeline()
    
    if (entering) {
      // Animate from extra position back to normal
      const fromX = side === 'left' ? '-110%' : '110%'
      const toX = side === 'left' ? '-100%' : '100%'
      
      timeline.fromTo(panelElement, 
        {
          x: fromX,
          force3D: true
        },
        {
          x: toX,
          duration: 0.35,
          ease: 'power3.out', // Smoother for route changes
          force3D: true,
          rotationZ: 0.01,
          scale: 1,
        }
      )
    } else {
      // Animate to extra position
      const currentX = side === 'left' ? '-100%' : '100%'
      const toX = side === 'left' ? '-110%' : '110%'
      
      timeline.fromTo(panelElement,
        {
          x: currentX,
          force3D: true
        },
        {
          x: toX,
          duration: 0.3,
          ease: 'power2.out',
          force3D: true,
          rotationZ: 0.01,
          scale: 1,
        }
      )
    }
    
    return new Promise<void>((resolve) => {
      timeline.eventCallback('onComplete', resolve)
    })
  }
  
  /**
   * Process queued animations
   */
  async function processQueue(): Promise<void> {
    if (animationQueue.value.length > 0 && globalAnimationState.value === 'idle') {
      const nextAnimation = animationQueue.value.shift()
      if (nextAnimation) {
        await nextAnimation()
      }
    }
  }
  
  /**
   * Cancel all animations and cleanup GSAP timelines
   */
  function cancelAnimations(): void {
    // Kill all active timelines
    activeTimelines.forEach(timeline => {
      timeline.kill()
    })
    activeTimelines.clear()
    
    // Reset state
    animationQueue.value = []
    globalAnimationState.value = 'idle'
    animatingPanel.value = null
  }
  
  /**
   * Check if a specific panel is currently animating
   */
  function isPanelAnimating(side: PanelSide): boolean {
    return animatingPanel.value === side && globalAnimationState.value !== 'idle'
  }
  
  /**
   * Check if any animation is running
   */
  function isAnyAnimationRunning(): boolean {
    return globalAnimationState.value !== 'idle'
  }
  
  /**
   * Cleanup function for component unmount
   */
  function cleanup(): void {
    cancelAnimations()
  }
  
  // Auto-cleanup on unmount
  onBeforeUnmount(cleanup)
  
  return {
    executeAnimation,
    switchPanels,
    animateRouteTransition,
    cancelAnimations,
    cleanup,
    isPanelAnimating,
    isAnyAnimationRunning,
    globalAnimationState: readonly(globalAnimationState),
    animatingPanel: readonly(animatingPanel)
  }
}

// Export readonly ref for external access
function readonly<T>(ref: { value: T }) {
  return computed(() => ref.value)
} 